import {Routes} from '@angular/router';
import {LngKeys} from "@app/shared/enums";
import {authGuard, dashboardGuard} from "@app/shared/guards";
import {route_paths} from "@app/shared/constants/languages_data";
import {canCreateBlogGuard} from "@app/shared/guards/can-create-blog.guard";
import {partnerPremiumGuard} from "@app/shared/guards/partner-premium.guard";
import {HomePageComponent} from "@app/layouts/main-layout/pages/home-page/home-page.component";

// remove the leading slash
const rls = (path: string) => {
  return path.replace(/^\//, '');
}

const gt = (route: string) => {
  const segments = route.split('/').filter(Boolean);

  return segments[segments.length - 1];
}

const get_tm_print = (lng: LngKeys) => {
  const str = 'template-print/3cc7a492-8c98-4133-8b3d-a1bef3f1c9ce/:documentId';
  if (lng === LngKeys.EN) {
    return str;
  }

  return `${lng}/${str}`;
}

const get_doc_page = (lng: LngKeys) => {
  return rls(`${route_paths[lng].documents}/:documentId`);
}

const other_routes: Routes = Object.values(LngKeys)
  .filter(key => key !== LngKeys.EN)
  .map((key: LngKeys) => {
    const routes: Routes = [
      {
        path: key,
        loadComponent: () => import('@app/layouts/main-layout/main-layout.component'),
        children: [
          {path: '', component: HomePageComponent, pathMatch: 'full'},
          {path: gt(route_paths[key].cv_templates), loadComponent: () => import('@app/layouts/main-layout/pages/cv-templates-page/cv-templates-page.component').then(m => m.CvTemplatesPageComponent)},
          {path: gt(route_paths[key].cover_letters), loadComponent: () => import('@app/layouts/main-layout/pages/cover-letters-page/cover-letters-page.component').then(m => m.CoverLettersPageComponent)},
          {path: gt(route_paths[key].pricing), loadComponent: () => import('@app/layouts/main-layout/pages/pricing-page/pricing-page.component').then(m => m.PricingPageComponent)},

          {path: gt(route_paths[key].terms_of_use), loadComponent: () => import('@app/pages/terms-of-use/terms-of-use.component').then(m => m.TermsOfUseComponent)},
          {path: gt(route_paths[key].refund_policy), loadComponent: () => import('@app/pages/refund-policy/refund-policy.component').then(m => m.RefundPolicyComponent)},
          {path: gt(route_paths[key].privacy_policy), loadComponent: () => import('@app/pages/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent)},
          {path: gt(route_paths[key].cookie_policy), loadComponent: () => import('@app/pages/cookie-policy/cookie-policy.component').then(m => m.CookiePolicyComponent)},
          {path: gt(route_paths[key].contact_us), loadComponent: () => import('@app/pages/contact-us/contact-us.component').then(m => m.ContactUsComponent)},
          {path: gt(route_paths[key].about_us), loadComponent: () => import('@app/pages/about-us/about-us.component').then(m => m.AboutUsComponent)},
          {path: gt(route_paths[key].ats_checker), loadComponent: () => import('@app/pages/ai-tools/ats-checker/ats-checker.component').then(m => m.AtsCheckerComponent)},
          {path: gt(route_paths[key].job_match), loadComponent: () => import('@app/pages/ai-tools/job-match/job-match.component').then(m => m.JobMatchComponent)},
          {path: gt(route_paths[key].ai_resume_generator), loadComponent: () => import('@app/pages/ai-tools/resume-generator/resume-generator.component').then(m => m.ResumeGeneratorComponent)},
          {path: gt(route_paths[key].ai_cover_letter_generator), loadComponent: () => import('@app/pages/ai-tools/ai-cover-letter-generator/ai-cover-letter-generator.component').then(m => m.AiCoverLetterGeneratorComponent)},
        ],
      },
      {
        path: rls(route_paths[key].auth),
        loadComponent: () => import('@app/layouts/auth/auth.component'),
        canActivate: [authGuard],
        children: [
          {path: '', redirectTo: gt(route_paths[key].sign_in), pathMatch: 'full'},
          {path: gt(route_paths[key].sign_in), loadComponent: () => import('@app/layouts/auth/pages/sign-in/sign-in.component').then(m => m.SignInComponent)},
          {path: gt(route_paths[key].sign_up), loadComponent: () => import('@app/layouts/auth/pages/sign-up/sign-up.component').then(m => m.SignUpComponent)},
          {path: gt(route_paths[key].forgot_password), loadComponent: () => import('@app/layouts/auth/pages/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)},
          {path: gt(route_paths[key].reset_password), loadComponent: () => import('@app/layouts/auth/pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)},
        ]
      },
      {
        path: rls(route_paths[key].account),
        loadComponent: () => import('@app/layouts/account/account.component'),
        canActivate: [dashboardGuard],
        children: [
          {path: '', redirectTo: gt(route_paths[key].user), pathMatch: 'full'},
          {path: gt(route_paths[key].user), loadComponent: () => import('@app/layouts/account/pages/user-page/user-page.component').then(m => m.UserPageComponent)},
          {path: gt(route_paths[key].security), loadComponent: () => import('@app/layouts/account/pages/change-password-page/change-password-page.component').then(m => m.ChangePasswordPageComponent)},
          {path: gt(route_paths[key].billing_history), loadComponent: () => import('@app/layouts/account/pages/billing-page/billing-page.component').then(m => m.BillingPageComponent)},
          {path: gt(route_paths[key].choose_plan), loadComponent: () => import('@app/layouts/account/pages/choose-plan/choose-plan.component').then(m => m.ChoosePlanComponent)},
        ]
      },
      {
        path: rls(route_paths[key].dashboard),
        loadComponent: () => import('@app/layouts/dashboard/dashboard.component'),
        canActivate: [dashboardGuard]
      },

      {
        path: get_doc_page(key),
        loadComponent: () => import('@app/layouts/builder/builder.component'),
        canActivate: [dashboardGuard]
      },

      {
        path: get_tm_print(key),
        loadComponent: () => import('@app/pages/tm-print-page/tm-print-page.component').then(m => m.TmPrintPageComponent)
      }
    ];

    return routes;
  })
  .flat();

const en_routes: Routes = [
  {
    path: '',
    loadComponent: () => import('@app/layouts/main-layout/main-layout.component'),
    children: [
      {path: '', component: HomePageComponent, pathMatch: 'full'},
      {path: rls(route_paths[LngKeys.EN].cv_templates), loadComponent: () => import('@app/layouts/main-layout/pages/cv-templates-page/cv-templates-page.component').then(m => m.CvTemplatesPageComponent)},
      {path: rls(route_paths[LngKeys.EN].cover_letters), loadComponent: () => import('@app/layouts/main-layout/pages/cover-letters-page/cover-letters-page.component').then(m => m.CoverLettersPageComponent)},
      {path: rls(route_paths[LngKeys.EN].pricing), loadComponent: () => import('@app/layouts/main-layout/pages/pricing-page/pricing-page.component').then(m => m.PricingPageComponent)},

      {path: rls(route_paths[LngKeys.EN].terms_of_use), loadComponent: () => import('@app/pages/terms-of-use/terms-of-use.component').then(m => m.TermsOfUseComponent)},
      {path: rls(route_paths[LngKeys.EN].refund_policy), loadComponent: () => import('@app/pages/refund-policy/refund-policy.component').then(m => m.RefundPolicyComponent)},
      {path: rls(route_paths[LngKeys.EN].privacy_policy), loadComponent: () => import('@app/pages/privacy-policy/privacy-policy.component').then(m => m.PrivacyPolicyComponent)},
      {path: rls(route_paths[LngKeys.EN].cookie_policy), loadComponent: () => import('@app/pages/cookie-policy/cookie-policy.component').then(m => m.CookiePolicyComponent)},
      {path: rls(route_paths[LngKeys.EN].contact_us), loadComponent: () => import('@app/pages/contact-us/contact-us.component').then(m => m.ContactUsComponent)},
      {path: rls(route_paths[LngKeys.EN].about_us), loadComponent: () => import('@app/pages/about-us/about-us.component').then(m => m.AboutUsComponent)},

      {path: 'confirm-email', loadComponent: () => import('@app/layouts/main-layout/pages/confirm-email/confirm-email.component').then(m => m.ConfirmEmailComponent)},
      {path: rls(route_paths[LngKeys.EN].ats_checker), loadComponent: () => import('@app/pages/ai-tools/ats-checker/ats-checker.component').then(m => m.AtsCheckerComponent)},
      {path: rls(route_paths[LngKeys.EN].job_match), loadComponent: () => import('@app/pages/ai-tools/job-match/job-match.component').then(m => m.JobMatchComponent)},
      {path: rls(route_paths[LngKeys.EN].ai_resume_generator), loadComponent: () => import('@app/pages/ai-tools/resume-generator/resume-generator.component').then(m => m.ResumeGeneratorComponent)},
      {path: rls(route_paths[LngKeys.EN].ai_cover_letter_generator), loadComponent: () => import('@app/pages/ai-tools/ai-cover-letter-generator/ai-cover-letter-generator.component').then(m => m.AiCoverLetterGeneratorComponent)},

      {
        path: 'resume-examples',
        loadComponent: () => import('@app/layouts/resume-examples/resume-examples.component'),
        children: [
          {path: '', loadComponent: () => import('@app/layouts/resume-examples/professions-categories/professions-categories.component').then(m => m.ProfessionsCategoriesComponent), pathMatch: 'full'},
          {path: 'category/:categoryName', loadComponent: () => import('@app/layouts/resume-examples/professions-sub-categories/professions-sub-categories.component').then(m => m.ProfessionsSubCategoriesComponent)},
          {path: ':profession', loadComponent: () => import('@app/layouts/resume-examples/profession-resume-article/profession-resume-article.component').then(m => m.ProfessionResumeArticleComponent)},
        ]
      },

      {
        path: 'career-blog',
        loadComponent: () => import('@app/layouts/career-blog/career-blog.component'),
        children: [
          {path: '', loadComponent: () => import('@app/layouts/career-blog/blog-list/blog-list.component').then(m => m.BlogListComponent), pathMatch: 'full'},
          {path: 'create', loadComponent: () => import('@app/layouts/career-blog/create-blog/create-blog.component').then(m => m.CreateBlogComponent), canActivate: [dashboardGuard, canCreateBlogGuard]},
          {path: ':profession', loadComponent: () => import('@app/layouts/career-blog/show-blog/show-blog.component').then(m => m.ShowBlogComponent)},
          {path: 'edit/:profession', loadComponent: () => import('@app/layouts/career-blog/create-blog/create-blog.component').then(m => m.CreateBlogComponent), canActivate: [dashboardGuard, canCreateBlogGuard]},
        ]
      },

      {
        path: 'partner-premium',
        loadComponent: () => import('@app/pages/partner-premium/partner-premium.component'),
        canActivate: [partnerPremiumGuard]
      },
    ],
  },
  {
    path: rls(route_paths[LngKeys.EN].auth),
    loadComponent: () => import('@app/layouts/auth/auth.component'),
    canActivate: [authGuard],
    children: [
      {path: '', redirectTo: gt(route_paths[LngKeys.EN].sign_in), pathMatch: 'full'},
      {path: gt(route_paths[LngKeys.EN].sign_in), loadComponent: () => import('@app/layouts/auth/pages/sign-in/sign-in.component').then(m => m.SignInComponent)},
      {path: gt(route_paths[LngKeys.EN].sign_up), loadComponent: () => import('@app/layouts/auth/pages/sign-up/sign-up.component').then(m => m.SignUpComponent)},
      {path: gt(route_paths[LngKeys.EN].forgot_password), loadComponent: () => import('@app/layouts/auth/pages/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent)},
      {path: gt(route_paths[LngKeys.EN].reset_password), loadComponent: () => import('@app/layouts/auth/pages/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)},
    ]
  },
  {
    path: rls(route_paths[LngKeys.EN].account),
    loadComponent: () => import('@app/layouts/account/account.component'),
    canActivate: [dashboardGuard],
    children: [
      {path: '', redirectTo: gt(route_paths[LngKeys.EN].user), pathMatch: 'full'},
      {path: gt(route_paths[LngKeys.EN].user), loadComponent: () => import('@app/layouts/account/pages/user-page/user-page.component').then(m => m.UserPageComponent)},
      {path: gt(route_paths[LngKeys.EN].security), loadComponent: () => import('@app/layouts/account/pages/change-password-page/change-password-page.component').then(m => m.ChangePasswordPageComponent)},
      {path: gt(route_paths[LngKeys.EN].billing_history), loadComponent: () => import('@app/layouts/account/pages/billing-page/billing-page.component').then(m => m.BillingPageComponent)},
      {path: gt(route_paths[LngKeys.EN].choose_plan), loadComponent: () => import('@app/layouts/account/pages/choose-plan/choose-plan.component').then(m => m.ChoosePlanComponent)},
    ]
  },
  {
    path: rls(route_paths[LngKeys.EN].dashboard),
    loadComponent: () => import('@app/layouts/dashboard/dashboard.component'),
    canActivate: [dashboardGuard],
  },
  {
    path: get_doc_page(LngKeys.EN),
    loadComponent: () => import('@app/layouts/builder/builder.component'),
    canActivate: [dashboardGuard]
  },
  {
    path: get_tm_print(LngKeys.EN),
    loadComponent: () => import('@app/pages/tm-print-page/tm-print-page.component').then(m => m.TmPrintPageComponent),
  },
];

export const routes: Routes = [
  ...en_routes,
  ...other_routes,

  {path: '**', loadComponent: () => import('@app/pages/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent)}
];
